// Dans l'appli iPhone (Capacitor charge le site en ligne et y injecte son pont natif `window.Capacitor`) :
// - les liens universels (lien de connexion, invitation) ouverts depuis WhatsApp arrivent ici et rechargent la page ;
// - des rappels locaux préviennent avant les échéances (questions du Conteur, serment des 24 h) ;
// - les notifications d'Apple arrivent quand le joueur est absent : le jeton de l'iPhone est donné au monde, qui l'utilise.
// Dans un navigateur, rien de tout cela ne s'active : `isNativeApp()` est faux.
import type { ClientView } from "../shared/types";
import { getState, send, subscribe } from "./store";

interface PluginListener { remove(): Promise<void> }
interface AppPlugin {
  /** selon la version de Capacitor : la poignée elle-même, ou une promesse de poignée */
  addListener(event: "appUrlOpen", fn: (e: { url: string }) => void): Promise<PluginListener> | PluginListener;
}
interface PendingNotification { id: number }
interface LocalNotificationsPlugin {
  requestPermissions(): Promise<{ display: string }>;
  checkPermissions(): Promise<{ display: string }>;
  getPending(): Promise<{ notifications: PendingNotification[] }>;
  cancel(opts: { notifications: PendingNotification[] }): Promise<void>;
  schedule(opts: { notifications: { id: number; title: string; body: string; schedule: { at: Date; allowWhileIdle?: boolean } }[] }): Promise<unknown>;
}
type Listening = Promise<PluginListener> | PluginListener;
interface PushNotificationsPlugin {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  register(): Promise<void>;
  addListener(event: "registration", fn: (e: { value: string }) => void): Listening;
  addListener(event: "registrationError", fn: (e: { error: unknown }) => void): Listening;
  addListener(event: "pushNotificationActionPerformed", fn: (e: { notification: { data?: { url?: unknown } } }) => void): Listening;
}
interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  /** PushNotifications : absent des versions de l'appli d'avant le 1er novembre 2026 */
  Plugins?: { App?: AppPlugin; LocalNotifications?: LocalNotificationsPlugin; PushNotifications?: PushNotificationsPlugin };
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
/** une question du Conteur : rappel une heure avant l'échéance */
const BEFORE_DEADLINE = HOUR;
/** le serment des 24 h : rappel trois heures avant que la flamme ne vacille */
const BEFORE_DRIFT = 3 * HOUR;
/** pas de rappel pour une échéance plus proche que ça (le joueur est sans doute déjà là) */
const MIN_LEAD = 5 * MINUTE;
const MAX_REMINDERS = 20;

function capacitor(): CapacitorGlobal | null {
  const c = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  return c && typeof c.isNativePlatform === "function" ? c : null;
}

/** Vrai dans l'appli iPhone (et pas dans Safari ni dans la PWA). */
export function isNativeApp(): boolean {
  try {
    return capacitor()?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

export interface Reminder { id: number; at: number; title: string; body: string }

function reminderId(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 1) % 2_000_000_000;
}

function parisTime(at: number): string {
  try {
    return new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" }).format(at);
  } catch {
    return new Date(at).toLocaleTimeString();
  }
}

/** Les rappels à programmer pour cette vue du monde (purs : testés sans téléphone). */
export function remindersFor(view: ClientView | null, now: number): Reminder[] {
  const me = view?.character;
  if (!view || !me || me.lifeState === "mort") return [];
  const out: Reminder[] = [];
  for (const s of view.sollicitations) {
    if (s.characterId !== me.id || s.status !== "ouverte") continue;
    const at = s.deadline - BEFORE_DEADLINE;
    if (at - now < MIN_LEAD) continue;
    out.push({
      id: reminderId(`sollicitation|${view.world.code}|${s.id}`),
      at,
      title: `Le Conteur attend ${me.name}`,
      body: `« ${s.title} » : réponds avant ${parisTime(s.deadline)}, sinon le destin choisira pour toi.`,
    });
  }
  if (me.driftAt) {
    const at = me.driftAt - BEFORE_DRIFT;
    if (at - now >= MIN_LEAD) {
      out.push({
        id: reminderId(`serment|${view.world.code}|${me.id}`),
        at,
        title: "Ta flamme vacille",
        body: `${me.name} n'a rien fait depuis longtemps dans ${view.world.name}. Agis avant ${parisTime(me.driftAt)} pour ne pas t'égarer.`,
      });
    }
  }
  return out.sort((a, b) => a.at - b.at).slice(0, MAX_REMINDERS);
}

let scheduled = "";
let asked = false;
let busy: Promise<void> = Promise.resolve();

async function syncReminders(plugin: LocalNotificationsPlugin, view: ClientView | null): Promise<void> {
  const wanted = remindersFor(view, Date.now());
  const signature = JSON.stringify(wanted.map((r) => [r.id, r.at]));
  if (signature === scheduled) return;
  if (!asked && wanted.length > 0) {
    asked = true;
    await plugin.requestPermissions().catch(() => undefined);
  }
  const permission = await plugin.checkPermissions().catch(() => ({ display: "denied" }));
  if (permission.display !== "granted") return;
  const pending = await plugin.getPending().catch(() => ({ notifications: [] as PendingNotification[] }));
  if (pending.notifications.length) await plugin.cancel({ notifications: pending.notifications.map((n) => ({ id: n.id })) }).catch(() => undefined);
  if (wanted.length) {
    await plugin.schedule({
      notifications: wanted.map((r) => ({ id: r.id, title: r.title, body: r.body, schedule: { at: new Date(r.at), allowWhileIdle: true } })),
    }).catch((err) => console.warn("[rappels]", err));
  }
  scheduled = signature;
}

/** Un lien universel ouvert depuis une autre appli : même site, on y va (le lien de connexion fait le reste). */
export function followLink(url: string, here: Location = window.location): void {
  let target: URL;
  try {
    target = new URL(url);
  } catch {
    return;
  }
  if (target.origin !== here.origin) return;
  const sameDocument = target.pathname === here.pathname && target.search === here.search;
  here.href = target.href;
  // seul le fragment change : le navigateur ne recharge pas, alors que l'accueil lit le lien au chargement
  if (sameDocument) here.reload();
}

/** Toucher une notification : ouvre son monde, sauf s'il est déjà à l'écran (le retour au premier plan suffit alors). */
export function openNotification(url: unknown, currentWorld: string | null, here: Location = window.location): void {
  if (typeof url !== "string") return;
  let target: URL;
  try {
    target = new URL(url, here.origin);
  } catch {
    return;
  }
  if (currentWorld !== null && target.searchParams.get("code") === currentWorld) return;
  followLink(target.href, here);
}

export type PushPermission = "inconnue" | "accordee" | "refusee";

let pushPermission: PushPermission = "inconnue";
let pushAsked = false;
let deviceToken: string | null = null;
let tokenGivenTo = "";

/** Ce que la personne a répondu à iOS pour les notifications. */
export function nativePushPermission(): PushPermission {
  return pushPermission;
}

/** Donne le jeton de l'iPhone au monde ouvert, une fois par monde et par héros. */
function giveToken(): void {
  const { session, connection } = getState();
  if (!deviceToken || !session || connection !== "connecte") return;
  const key = `${session.code}/${session.playerId}/${deviceToken}`;
  if (key === tokenGivenTo) return;
  tokenGivenTo = key;
  send({ t: "apns_register", token: deviceToken });
}

/** iOS ne pose sa question qu'une fois, pour les rappels comme pour les notifications. */
async function askForPush(plugin: PushNotificationsPlugin): Promise<void> {
  const current = await plugin.checkPermissions();
  const answer = current.receive.startsWith("prompt") ? await plugin.requestPermissions() : current;
  pushPermission = answer.receive === "granted" ? "accordee" : "refusee";
  if (pushPermission === "accordee") await plugin.register();
}

function listenToPush(plugin: PushNotificationsPlugin): void {
  quietly(() => plugin.addListener("registration", (e) => {
    deviceToken = e.value;
    giveToken();
  }));
  quietly(() => plugin.addListener("registrationError", (e) => console.warn("[notifications]", e.error)));
  quietly(() => plugin.addListener("pushNotificationActionPerformed", (e) => {
    openNotification(e.notification.data?.url, getState().session?.code ?? null);
  }));
}

/** Selon la version de Capacitor, un plugin répond par une valeur ou une promesse : une erreur ne doit jamais remonter. */
function quietly(start: () => unknown): void {
  try {
    void Promise.resolve(start()).catch((err) => console.warn("[appli]", err));
  } catch (err) {
    console.warn("[appli]", err);
  }
}

/** À appeler une fois au lancement. Ne lève jamais : une erreur ici laisserait l'appli sur un écran vide. */
export function initNative(): void {
  try {
    startNative();
  } catch (err) {
    console.warn("[appli]", err);
  }
}

function startNative(): void {
  const c = capacitor();
  if (!c || !isNativeApp()) return;
  document.documentElement.classList.add("app-native");
  quietly(() => c.Plugins?.App?.addListener("appUrlOpen", (e) => followLink(e.url)));
  const notifications = c.Plugins?.LocalNotifications;
  const push = c.Plugins?.PushNotifications;
  if (push) listenToPush(push);
  if (!notifications && !push) return;
  const run = (): void => {
    const view = getState().view;
    giveToken();
    busy = busy.then(async () => {
      // la question d'iOS arrive quand il y a un héros à prévenir, pas sur l'écran d'accueil
      if (push && view?.character && !pushAsked) {
        pushAsked = true;
        await askForPush(push);
      }
      if (notifications) await syncReminders(notifications, view);
    }).catch(() => undefined);
  };
  subscribe(run);
  run();
}
